import axios from 'axios';

import BaseService from "./BaseService";
import LogUtils from "../utils/LogUtils";
import settings from "../settings/settings";

class AgentInspectionService extends BaseService {

  constructor() {
    super();
    this.TAG = "AgentInspectionService";

    this.logUtils = new LogUtils();
  }

  async getAgentInspectionByDenounceId(denounceId) {
    let agentInspection = {};

    try {
      const response = this.getResponse(await axios(this.getBasicHttpRequest(
        this.HTTP_METHODS.GET,
        `${settings.MANAGEMENT_SERVICE_URL}/agent-inspections/denounce/${denounceId}`,
        this.getDefaultHeaders(true)
      )));

      if (response.status !== 1) {
        return {
          status: 0,
          msg: "Não foi possível retornar informações sobre a inspeção."
        };
      }

      if (!response.data) {
        return {
          status: -1,
          msg: "Não foi possível retornar informações sobre a inspeção."
        };
      }

      agentInspection = response.data;

    } catch (err) {
      this.logUtils.logErrorMsg(this.TAG, "Unabled to getAgentInspectionByDenounceId", err);

      return {
        status: -2,
        msg: "Não foi possível retornar informações sobre a inspeção"
      };
    }

    return {
      status: 1,
      msg: "As informações sobre as inspeção foram retornadas com sucesso.",
      data: agentInspection
    };
  }

  async createAgentInspection({ addressId, populationUserId, agentUserId, denounceId, agentFeedback }) {
    let agentInspection = null;

    try {
      const response = this.getResponse(await axios(
        this.getBasicHttpRequest(this.HTTP_METHODS.POST,
          `${settings.MANAGEMENT_SERVICE_URL}/agent-inspections`,
          this.getDefaultHeaders(true, true),
          {
            scheduled: true,
            verifiedByOwnAgent: true,
            addressId: addressId,
            populationUserId: populationUserId,
            agentUserId: agentUserId,
            denounceId: denounceId,
            agentFeedback: agentFeedback
          },
          true
        )));

      if (response.status !== 1) {
        return {
          status: 0,
          msg: response.error ? response.error : "Não foi possível criar a inspeção."
        };
      }

      if (!response.data) {
        return {
          status: -1,
          msg: "Houve um erro ao processar a operação."
        };
      }

      agentInspection = response.data;

    } catch (err) {
      this.logUtils.logErrorMsg(this.TAG, "Unabled to createAgentInspection", err);

      return {
        status: -2,
        msg: "Não foi possível criar a inspeção"
      };
    }

    return {
      status: 1,
      msg: "Inspeção criada com sucesso.",
      data: agentInspection
    };
  }

  async updateAgentInspection({ id, agentInspectionStatusId, addressId, populationUserId,
    agentUserId, denounceId, closurePictureId, agentFeedback }) {
    try {
      const response = this.getResponse(await axios(
        this.getBasicHttpRequest(this.HTTP_METHODS.PUT,
          `${settings.MANAGEMENT_SERVICE_URL}/agent-inspections/`,
          this.getDefaultHeaders(true, true),
          {
            id: id,
            agentInspectionStatusId: agentInspectionStatusId,
            addressId: addressId,
            populationUserId: populationUserId,
            agentUserId: agentUserId,
            denounceId: denounceId,
            agentFeedback: agentFeedback,
            closurePictureId: closurePictureId
          },
          true
        )));

      if (response.status !== 1) {
        return {
          status: 0,
          msg: response.error ? response.error : "Não foi possível atualizar a inspeção."
        };
      }

      if (!response.data) {
        return {
          status: -1,
          msg: "Houve um erro ao processar a operação."
        };
      }

    } catch (err) {
      this.logUtils.logErrorMsg(this.TAG, "Unabled to updateAgentInspection", err);

      return {
        status: -2,
        msg: "Não foi possível atualizar a inspeção"
      };
    }

    return {
      status: 1,
      msg: "Inspeção atualizada com sucesso."
    };
  }

  async upsertAgentInspection({ agentInspectionStatusId, addressId, populationUserId, agentUserId,
    denounceId, agentFeedback, closurePictureId }) {
    const resOpGetInspection = await this.getAgentInspectionByDenounceId(denounceId);
    const inspection = resOpGetInspection?.data?.[0];

    if (inspection) {
      inspection.agent_user_id = agentUserId || inspection.agent_user_id;
      inspection.agent_feedback = agentFeedback || inspection.agent_feedback;
      inspection.closure_picture_id = closurePictureId || inspection.closure_picture_id;
      inspection.agent_inspection_status_id = agentInspectionStatusId || inspection.agent_inspection_status_id;

      const resUpdate = await this.updateAgentInspection({
        id: inspection.id,
        addressId: inspection.address_id,
        populationUserId: inspection.population_user_id,
        denounceId: inspection.denounce_id,
        agentUserId: inspection.agent_user_id,
        agentInspectionStatusId: inspection.agent_inspection_status_id,
        agentFeedback: inspection.agent_feedback,
        closurePictureId: inspection.closure_picture_id
      });
      resUpdate.data = inspection;
      return resUpdate;
    } else {
      return await this.createAgentInspection({
        addressId,
        populationUserId,
        agentUserId,
        denounceId,
        agentFeedback
      });
    }
  }

  async updateAgentInspectionWithCode({ id, populationUserId, agentActualPoints, validationCode }) {
    try {
      const response = this.getResponse(await axios(
        this.getBasicHttpRequest(this.HTTP_METHODS.PUT,
          `${settings.MANAGEMENT_SERVICE_URL}/agent-inspections/${id}/grade`,
          this.getDefaultHeaders(true, true),
          {
            id,
            populationUserId,
            agentActualPoints,
            validationCode
          },
          true
        )));

      if (response.status !== 1) {
        return {
          status: 0,
          msg: response.error ? response.error : "Não foi possível atualizar a inspeção com código do usuário."
        };
      }

      if (!response.data) {
        return {
          status: -1,
          msg: "Houve um erro ao processar a operação."
        };
      }

    } catch (err) {
      this.logUtils.logErrorMsg(this.TAG, "Unabled to updateAgentInspectionWithCode", err);

      return {
        status: -2,
        msg: "Não foi possível atualizar a inspeção com código do usuário"
      };
    }

    return {
      status: 1,
      msg: "Inspeção atualizada com sucesso."
    };
  }

}

export default AgentInspectionService;